import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { IMAGES } from "@/lib/constants";
import { useLanguage } from "@/i18n/LanguageContext";
import Reveal from "@/components/Reveal";

const SLIDE_IMAGES = [
  IMAGES.cathodesWarehouse,
  IMAGES.cathodesBundles,
  IMAGES.hero,
  IMAGES.shipping,
  IMAGES.operations,
];

const OperationsSlider = () => {
  const { t } = useLanguage();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    const id = setInterval(() => emblaApi.scrollNext(), 5000);
    return () => clearInterval(id);
  }, [emblaApi]);

  const captions = t("home.gallery.slides");

  return (
    <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8" data-testid="operations-gallery">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono2 text-xs uppercase tracking-[0.35em] text-[#9db4ff]">
              {t("home.gallery.kicker")}
            </p>
            <h2 className="font-display mt-3 text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
              {t("home.gallery.title")}
            </h2>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="gallery-prev-button"
              onClick={() => emblaApi && emblaApi.scrollPrev()}
              aria-label="Previous slide"
              className="border border-[#4a6cf7]/50 p-3 text-[#9db4ff] transition-colors duration-300 hover:bg-[#4a6cf7] hover:text-white"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              data-testid="gallery-next-button"
              onClick={() => emblaApi && emblaApi.scrollNext()}
              aria-label="Next slide"
              className="border border-[#4a6cf7]/50 p-3 text-[#9db4ff] transition-colors duration-300 hover:bg-[#4a6cf7] hover:text-white"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.12}>
        <div className="mt-10 overflow-hidden border border-[#4a6cf7]/25" ref={emblaRef}>
          <div className="flex">
            {SLIDE_IMAGES.map((src, i) => (
              <div
                key={src}
                data-testid={`gallery-slide-${i}`}
                className="relative min-w-0 flex-[0_0_100%] sm:flex-[0_0_60%] lg:flex-[0_0_45%]"
              >
                <img
                  src={src}
                  alt={captions[i]}
                  className="aspect-[16/10] w-full border-r border-[#4a6cf7]/25 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a192f]/90 via-transparent to-transparent" />
                <p className="font-mono2 absolute bottom-4 left-4 right-4 text-[10px] uppercase tracking-[0.25em] text-[#9db4ff] sm:text-xs">
                  {captions[i]}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-5 flex justify-center gap-2">
          {SLIDE_IMAGES.map((_, i) => (
            <button
              key={i}
              data-testid={`gallery-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
              className={`h-1.5 transition-all duration-300 ${
                selected === i ? "w-8 bg-[#4a6cf7]" : "w-3 bg-white/20 hover:bg-white/40"
              }`}
            />
          ))}
        </div>
      </Reveal>
    </section>
  );
};

export default OperationsSlider;
